/**
 * en-US — the CANONICAL org-admin dictionary. Every user-visible string in
 * org-admin components/pages lives here, keyed by component namespace.
 *
 * ⚠️ Values must stay byte-identical to the strings that were hardcoded in
 * the components before the i18n extraction (2026-07-14): LexPortal renders
 * org-admin WITHOUT a locale (defaults to en-US), so any drift here is a
 * silent visual regression on the platform tool. `OrgAdminDict` (types.ts)
 * derives its shape from this object — pt-BR/es-ES must mirror every key.
 *
 * NOT translated anywhere (by design): provider ids/names used as
 * identifiers (anthropic, OpenRouter…), model ids, tier KEY names
 * (MAX/HIGH/MED/LOW), thinking-level wire values, code/URL placeholders,
 * data-testid values, console/log strings.
 */
export const enUS = {
    common: {
        save: "Save",
        cancel: "Cancel",
        add: "Add",
        edit: "Edit",
        done: "Done",
        retry: "Retry",
        copy: "Copy",
        copied: "Copied",
        dismiss: "Dismiss",
        hide: "Hide",
        show: "Show",
        connect: "Connect",
        disconnect: "Disconnect",
        name: "Name",
        email: "Email",
        role: "Role",
        status: "Status",
        type: "Type",
        user: "User",
        optional: "optional",
    },
    nav: {
        users: "Users",
        settings: "Settings",
        aiProviders: "AI Providers",
        auditLog: "Audit Log",
        tasks: "Tasks",
    },
    saveDock: {
        saving: "Saving…",
        fixToSave: "Fix to save",
        saveChanges: "Save changes",
        saved: "Saved",
        noChanges: "No changes",
        unsavedHint: "Unsaved changes — auto-saves within 15s",
        ariaSave: "Save AI provider configuration",
    },
    config: {
        loading: "Loading runtime configuration...",
        sectionCredentials: "LLM Credentials",
        sectionLegacy: "Legacy Provider Fields",
        sectionOauth: "OAuth Connections",
        sectionOpenrouter: "OpenRouter / Custom Provider",
        sectionModelDefaults: "Model Defaults",
        sectionAliases: "Model Aliases",
        sectionAgent: "Agent Behavior",
        legacyFields: {
            anthropicOauthToken: "Anthropic OAuth Token",
            anthropicApiKey: "Anthropic API Key",
            openaiApiKey: "OpenAI API Key",
            googleApiKey: "Google API Key",
            xaiApiKey: "xAI API Key",
        },
        legacyPresenceHelp: "Read-only — the table above is the credential editor. These values stay encrypted at rest as a dormant fallback and are shadowed by a table entry for the same provider (migrated entries carry a “migrated: …” label).",
        legacyNone: "No legacy provider fields stored — the table above is the only credential store.",
        oauthLoading: "Loading OAuth status…",
        oauthHelp: "Subscription OAuth uses a manual-code flow — Lexiun never sees your ChatGPT password. Tokens are stored encrypted and refreshed centrally before expiry. Disconnecting clears them immediately.",
        openrouterLoading: "Loading provider status…",
        configured: "Configured",
        notConfigured: "Not configured.",
        openrouterHelp: "Read-only — add or edit OpenRouter / custom endpoints as rows in the LLM Credentials table above (§8.10). An existing legacy blob keeps serving as a dormant fallback; Disconnect clears it.",
        openrouterDisconnectConfirm: "Disconnect OpenRouter? Chats relying on this credential will start failing.",
        openrouterDisconnected: "OpenRouter disconnected.",
        chatProfile: "Chat profile",
        resolving: "Resolving…",
        resolvesTo: "Resolves to",
        resolvesThinking: "· thinking=",
        resolvesMaxOut: "· max-out=",
        profileHelpA: "Resolves through the platform's",
        profileHelpB: "catalog. Composite",
        profileHelpC: "overlays the thinking level on top of the profile's policy.",
        budgetPerTask: "Budget Per Task",
        aliasesHelpBeforePrimary: "Map capability tiers + custom task aliases to a provider/model, with an optional fallback. Harnesses + the chat default resolve through this table; a provider outage descends from",
        aliasesHelpPrimary: "Primary",
        aliasesHelpTo: "to",
        aliasesHelpFallback: "Fallback",
        aliasesHelpAfter: "automatically. The badge on each alias shows what it resolves to right now.",
        thinkingLevel: "Thinking Level",
        notSet: "Not set",
        thinkingLow: "Low",
        thinkingMedium: "Medium",
        thinkingHigh: "High",
        maxAgentTurns: "Max Agent Turns",
        systemPromptPrefix: "System Prompt Prefix",
        systemPromptPlaceholder: "Org-specific instructions prepended to every agent run...",
        systemPromptHelp: "System prompt prefix is not yet applied by the runtime (forward-compatible).",
        lastUpdated: "Last updated: {date}",
        lastUpdatedBy: " by {user}",
        noChangesToSave: "No changes to save",
        autoSaved: "Auto-saved. Changes sync to runtime within 60 seconds.",
        configSaved: "Configuration saved. Changes sync to runtime within 60 seconds.",
        validationFailed: "Real-LLM validation failed: {label}{detail}{latency}. The default model was NOT saved.",
        previewRequestFailed: "Preview request failed",
        blockingCredential: "Credential {detail}. Fill it in or remove the row.",
        blockingAlias: "Model alias {detail}. Fill it in or remove the row.",
        validationCodes: {
            auth: "Authentication failed (credential is wrong or expired)",
            "model-unknown": "Model unknown to the provider",
            quota: "Quota / rate limit exceeded",
            network: "Network error reaching the provider",
            schema: "Server rejected the request shape",
            unknown: "Validation failed (unknown reason)",
        },
    },
    oauth: {
        openaiCodexDisplayName: "OpenAI / ChatGPT subscription",
        connected: "Connected",
        connectedMsg: "Connected.",
        disconnectedMsg: "Disconnected.",
        connectionBroken: "Connection broken",
        degraded: "Degraded",
        reconnect: "Reconnect",
        awaitingCode: "Awaiting authorisation code…",
        emptyValidation: "Paste the redirect URL (or code) from the provider.",
        disconnectConfirm: "Disconnect {name}? Existing chats relying on this credential will start failing.",
        step1: "1. Open the sign-in page below (new tab), sign in to {name}, and authorize.",
        step2: '2. After you authorize, your browser lands on a localhost page that either says "Authentication successful" or fails to load ("can\'t reach this site") — either is fine. Copy the entire URL from the browser\'s address bar (it contains the authorization code=…).',
        step3: "3. Paste that URL here and click Complete:",
        urlPlaceholder: "paste the full localhost redirect URL",
        connecting: "Connecting…",
        complete: "Complete",
    },
    searchProviders: {
        title: "Search providers",
        help: "Keys used for concierge web search. A key set here belongs to this organization; without one, the platform default is used when available.",
        srcOrg: "organization key",
        srcPlatform: "platform default",
        srcNone: "not configured",
        disabledNote: "search is turned off for this organization — the key below is kept",
    },
    credentials: {
        colId: "ID",
        colProvider: "Provider",
        colKeySecret: "Key / Secret",
        colEndpoint: "Endpoint",
        addCredential: "Add credential",
        emptyRow: "No credentials yet — add a row, or rely on any read-only legacy values still stored below.",
        help: "This table is the credential editor — one row per key/endpoint; rows take precedence over any read-only legacy value below for the same provider. Saved keys show as ***MASKED*** — leave masked to keep the stored value. ChatGPT-subscription OAuth is connected in the OAuth Connections panel. Saving replaces the whole table.",
        providerLabels: {
            anthropic: "Anthropic",
            openai: "OpenAI (API key)",
            google: "Google",
            xai: "xAI",
            openrouter: "OpenRouter",
            custom: "Custom (OpenAI-compatible)",
        },
        ariaId: "credential id",
        ariaProvider: "provider",
        ariaSecret: "credential secret",
        ariaBaseUrl: "base url",
        ariaModels: "models",
        idCollision: "A credential with this id already exists",
        renameLocked: "Re-enter the key/secret before renaming — a stored secret can't move to a new id",
        secretPlaceholder: "key / token",
        maskedHidden: "Stored value stays hidden",
        baseUrlPlaceholderOpenrouter: "https://openrouter.ai/api/v1 (default)",
        baseUrlPlaceholder: "base URL",
        modelsPlaceholder: "model ids (comma-separated)",
        deleteCredential: "Delete credential",
        validation: {
            missingSecret: "{id} is missing its key/secret",
            secretTooLong: "{id}'s secret is too long (max 8192 chars)",
            invalidId: '"{id}" is not a valid id (kebab-case slug)',
            idTooLong: '"{id}" is too long (max {max} chars)',
            kindCustom: '{id}: provider "{provider}" takes kind "custom"',
            kindApiKey: '{id}: provider "{provider}" takes kind "api-key"',
            baseUrlRequired: '{id}: provider "custom" requires a base URL (only OpenRouter has a default)',
            baseUrlTooLong: "{id}: base URL is too long (max 400 chars)",
            baseUrlInvalid: "{id}: base URL is not a valid URL",
            tooManyModels: "{id}: at most 16 model ids",
            modelIdTooLong: '{id}: model id "{model}…" is too long (max 200 chars)',
        },
    },
    aliases: {
        addCustomAlias: "Add custom alias",
        helpBeforeDefault: "Preset tiers always resolve — reverting a preset to",
        helpDefaultWord: "Default",
        helpAfterDefault: "uses the platform catalog. Custom aliases must be SHOUTY_SNAKE_CASE (e.g. WRITER, PETITION_DRAFTER). Saving replaces the whole table.",
        modeDefault: "Default",
        modePin: "Pin",
        modeDelegate: "Delegate",
        modeDefaultTitle: "Resolve via the platform catalog",
        modePinTitle: "Pin a provider/model ladder for this org",
        modeDelegateTitle: "Resolve whatever another alias resolves",
        ariaRowMode: "row mode",
        ariaAliasName: "alias name",
        ariaDelegateTarget: "delegate target",
        ariaProvider: "provider",
        ariaModel: "model",
        ariaThinking: "thinking",
        nameCollision: "An alias with this name already exists",
        revertTitle: "Revert to platform default",
        deleteAlias: "Delete alias",
        delegatesTo: "delegates to",
        delegateHint: "— resolves whatever that alias resolves",
        resolvesViaPlatform: "resolves via platform catalog",
        outageDescends: "Descends automatically on a provider outage",
        removeFallback: "Remove fallback",
        addFallback: "Add fallback",
        inherit: "(inherit)",
        modelPlaceholder: "model id",
        modelRequired: "Model id is required",
        noCredentialTitle: "The org has no credential for this provider — the save will be rejected",
        noCredentialSuffix: " (no credential)",
        thinkingOverlayTitle: "Thinking effort overlay",
        rungPrimary: "1 · Primary",
        rungFallback: "2 · Fallback",
        badges: {
            platform: "platform",
            primary: "primary",
            fallback: "fallback",
            rescue: "rescue",
            unavailable: "unavailable",
        },
        providerLabels: {
            "openai-codex": "OpenAI Codex (ChatGPT OAuth)",
            openai: "OpenAI (API key)",
            anthropic: "Anthropic",
            google: "Google",
            xai: "xAI",
            openrouter: "OpenRouter",
            custom: "Custom",
        },
        validation: {
            primaryMissingModel: "{alias} (primary) is missing a model id",
            fallbackMissingModel: "{alias} (fallback) is missing a model id",
        },
    },
    users: {
        allStatuses: "All statuses",
        statusActive: "Active",
        statusProvisioning: "Provisioning",
        statusDeactivated: "Deactivated",
        allRoles: "All roles",
        roleOrgAdmin: "Org Admin",
        roleMember: "Member",
        addUser: "Add User",
        deactivateConfirm: "Deactivate {name}?",
        revokeConfirm: "Revoke the pending invitation for {name}? The link stops working.",
        acceptConfirm: "Accept the invitation for {name} ({email}) on their behalf?\n\nThis creates a Clerk identity with a known temporary password and marks the invitation as accepted. Intended for:\n  • +clerk_test accounts (no real email delivery), or\n  • a real user who lost their invitation link.\n\nIf this is a real user, you MUST tell them to use Clerk's \"Forgot password\" flow immediately to set their own password.",
        failedDeactivate: "Failed to deactivate user",
        failedResend: "Failed to resend invitation",
        failedRevoke: "Failed to revoke invitation",
        failedAccept: "Failed to accept invitation",
        freshInvitationUrl: "Fresh invitation URL for {email}",
        emailDeliveryFailed: "The invitation was created but could NOT be delivered to {email}. Share the link below directly.",
        emailDeliveryFailedNoUrl: "The email was updated, but the invitation could not be delivered to {email} and no shareable link was returned. Use Resend on their row to try again.",
        loadingUsers: "Loading users...",
        colInvitation: "Invitation",
        resendTitle: "Resend invitation",
        resendLabel: "Resend",
        revokeTitle: "Revoke invitation",
        acceptTitle: "Accept invitation on user's behalf (admin unblock — operator must instruct real users to reset their password via Clerk's Forgot password flow)",
        deactivateTitle: "Deactivate user",
        editTitle: "Edit user",
        noUsersFound: "No users found",
    },
    editUser: {
        title: "Edit {name}",
        failedSave: "Failed to save changes",
        emailReissueHint: "Changing this revokes the pending invitation and sends a new one to the new address.",
        emailLockedAccepted: "This user has already signed in, so this address is now their sign-in credential and can only be changed in Clerk.",
        emailLockedUnknown: "This user has no pending invitation, so their sign-in state cannot be confirmed from here and the address is left alone.",
        emailLockedTitle: "Email can only be changed while an invitation is still pending",
        errNotEditable: "This user's email address cannot be changed.",
        errReissueFailed: "The address was saved, but the invitation could not be re-issued. Use Resend on this user — submitting this form again will not re-send it.",
        errDuplicate: "Another user in this organization already uses {email}.",
        emailNotApplied: "The server saved the other changes but did not apply the new email — it is running a build without email-edit support. The address is unchanged.",
    },
    invite: {
        failedCreate: "Failed to create user",
        sentTitle: "Invitation sent",
        sentBody1: "A Clerk invitation email is being sent to ",
        sentBody2: ". They click the link, set a password, and sign in normally. Need the link directly (e.g. a ",
        sentBody3: " address that won't receive real email)? Use the ",
        resend: "Resend",
        sentBody4: " action on their row once provisioning completes — it returns a fresh shareable URL.",
        emailPlaceholder: "Email",
        firstNamePlaceholder: "First name",
        lastNamePlaceholder: "Last name",
    },
    settings: {
        failedLoad: "Failed to load settings",
        failedSave: "Failed to save settings",
        loading: "Loading settings...",
        couldNotLoad: "Could not load settings",
        title: "Organization Settings",
        saved: "Settings saved",
        maxUsers: "Max Users",
        tier: "Tier",
        tierFree: "Free",
        tierStarter: "Starter",
        tierProfessional: "Professional",
        tierEnterprise: "Enterprise",
        saveSettings: "Save Settings",
    },
    audit: {
        failedLoad: "Failed to load audit logs",
        title: "Audit Log",
        filterPlaceholder: "Filter by action...",
        loading: "Loading audit logs...",
        colTime: "Time",
        colUser: "User",
        colAction: "Action",
        colEntity: "Entity",
        colDetails: "Details",
        noEntries: "No audit entries found",
    },
    tasks: {
        title: "Tasks",
        comingSoon: "Org-scoped task monitoring — coming soon",
    },
    badges: {
        status: {
            ACTIVE: "ACTIVE",
            SUSPENDED: "SUSPENDED",
            PROVISIONING: "PROVISIONING",
            DELETED: "DELETED",
            TERMINATED: "TERMINATED",
            DEACTIVATED: "DEACTIVATED",
            DEACTIVATING: "DEACTIVATING",
        },
        tier: {
            free: "free",
            starter: "starter",
            professional: "professional",
            enterprise: "enterprise",
        },
        role: {
            ORG_ADMIN: "ORG_ADMIN",
            MEMBER: "MEMBER",
        },
        storageMode: {
            shared: "shared",
            dedicated: "dedicated",
        },
        storageModeTitle: {
            shared: "Shared pool — org data lives in a multi-tenant Premium NFS share with kernel-enforced subPath isolation. ~R$0/mo marginal storage cost.",
            dedicated: "Dedicated share — org has its own Premium NFS share with per-share ACL. ~R$130/mo storage floor per org.",
        },
        storageTitleFallback: "storage: {mode}",
        clientStatus: {
            active: "active",
            prospect: "prospect",
            inactive: "inactive",
            former: "former",
        },
        matterStatus: {
            active: "active",
            paused: "paused",
            closed: "closed",
        },
        matterType: {
            civil: "civil",
            labor: "labor",
            tax: "tax",
            contract: "contract",
            advisory: "advisory",
            corporate: "corporate",
            realestate: "realestate",
            compliance: "compliance",
            other: "other",
        },
        assignmentRole: {
            LEAD: "LEAD",
            HANDLER: "HANDLER",
            CONTRIBUTOR: "CONTRIBUTOR",
            OBSERVER: "OBSERVER",
        },
        invitation: {
            sent: "Sent",
            accepted: "Accepted",
            revoked: "Revoked",
            expired: "Expired",
        },
    },
    clients: {
        placeholderPerson: "João da Silva",
        placeholderCompany: "Acme Brasil Ltda.",
        company: "Company",
        person: "Person",
        primaryContactUserId: "Primary contact user ID",
        alias: "Reference handle (@alias)",
        /** Edit-only: a client is created first, then given a handle. The server
         *  coerces to the tag grammar and dedupes per org, so this is a request,
         *  not the final value — say so rather than implying it saves verbatim. */
        aliasHelp: "Used to reference this client as @handle. The server normalizes the format and ensures it is unique in the org. Leave empty to remove it.",
        createClient: "Create client",
        loadingClients: "Loading clients...",
        colMatters: "Matters",
        noClientsYet: "No clients yet",
        aliasTitle: "Client handle (@alias)",
        selfBadge: "self",
        selectClient: "Select a client...",
        selfSuffix: " (self)",
    },
    /** ClientDeleteConfirm — the 2026-07-30 v2 hard-delete ruling. Copy rules:
     *  the client delete IS permanent (say so); the cases are NOT deleted (say
     *  so). Counts use neutral phrasing so no plural agreement is needed. */
    clientDelete: {
        confirmPhrase: "I UNDERSTAND",
        title: "Delete this client permanently?",
        cleanBody: "This action is PERMANENT and cannot be undone. The client record and its empty matters will be removed.",
        impactTitle: "Delete client with cases?",
        impactBody: "This client will be PERMANENTLY deleted. Its cases will NOT be deleted — linked cases: {count} (in the recycle bin: {binned}). They will move to “Unassigned”, with no client or matter.",
        matterCases: "active: {live} · recycle bin: {binned}",
        blockedTitle: "This client cannot be deleted yet",
        blockedBody: "Legacy-format cases depend on this client's folder — blocking cases: {count}. Reassign or migrate them first.",
        blockedAck: "Got it",
        typeToConfirm: "Type to confirm:",
        deleteAction: "Delete client",
    },
    matters: {
        namePlaceholder: "Consumer Litigation Defense",
        matterType: "Matter type",
        typeImmutable: "Matter type is immutable after creation.",
        typeFixed: "Matter type is fixed for this creation flow.",
        primaryLeadUserId: "Primary lead user ID",
        createMatter: "Create matter",
        loadingMatters: "Loading matters...",
        colCases: "Cases",
        noMattersYet: "No matters yet",
        selectMatter: "Select a matter...",
    },
    assignments: {
        addUserTo: {
            client: "Add user to client",
            matter: "Add user to matter",
            case: "Add user to case",
        },
        userId: "User ID",
        addAssignment: "Add assignment",
        loading: "Loading assignments...",
        scopeLabels: {
            client: "Client assignments",
            matter: "Matter assignments",
            case: "Case assignments",
        },
        directSuffix: " — direct",
        revokeTitle: "Revoke assignment",
        noDirect: "No direct assignments.",
        inheritedTitle: "Inherited from parent scope",
        colInheritedFrom: "Inherited from",
        parent: "parent",
    },
    profilePicker: {
        profileDefault: "(profile default)",
        unset: "(unset)",
        thinkingLabel: "thinking:",
        ariaBaseProfile: "Base profile",
        ariaThinkingOverlay: "Thinking overlay",
    },
};
//# sourceMappingURL=en-US.js.map